import dayjs from 'dayjs';
import { getStoredSizes } from '@/lib/utils';
import { createPortal } from 'react-dom';
import { AlertCircle, Locate, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Button, Combobox, Input, Label, LocationInput, MultiSelectDropdown, Textarea, useToast } from '@/components';
import { useERSContext, useMapContext } from '@/context';
import {
  capitalizeWords,
  constants,
  fetchRouteAndUpdateResponder,
  getAddressFromCoord,
  getCurrentCoord,
  INPUT_LIMITS,
  isValidCoordinates,
  mapbox_accessToken,
  parseGPSInput,
  SUBMIT_DEBOUNCE_MS,
  sanitizeInput,
  severityValues,
  updateRespondersDistance,
  validationRules,
  translatedTitle,
} from '@/lib';
import { ResponderDetail } from '.';

const asterisk = "after:content-['*'] after:text-red-500 after:ml-1";

const statusValues = [
  {
    value: 'in_progress',
    label: 'Progress',
  },
  {
    value: 'resolved',
    label: 'Closed',
  },
];

// Helper to get initial form data
const getInitialFormData = (incidentData, profile, incidentTypesOptions) => {
  const original = incidentData?.original;

  // Normalize status to handle variations like 'progress' or 'in_progress'
  const normalizedStatus = original?.status === 'progress' ? 'in_progress' : original?.status;

  // Find matching status in available options
  let initialStatus = statusValues?.find(item => item.value === normalizedStatus) || null;

  // If status is 'created', keep it as is but note it won't be in dropdown
  // The user can see it but cannot change back to it
  if (original?.status === 'created') {
    initialStatus = {
      value: 'open',
      label: 'Created',
    };
  }

  return {
    id: original?.id || uuidv4(),
    organizationId: profile?.organizationId || '',
    selectedIncidentType: incidentTypesOptions?.find(item => item.value === original?.type) || null,
    selectedSeverity: severityValues?.find(item => item.value === original?.severity) || null,
    selectedStatus: initialStatus, // Use the actual current status
    description: original?.description || '',
    assignees: original?.assignees,
    geoloc: original?.geoloc || null,
    location: original?.location?.display_name || '',
    location_description: original?.location?.location_description || '',
    title: original?.title || '',
    creationTimestamp: original?.creationTimestamp || dayjs().unix(),
    created_by: {
      display_name: original?.created_by?.display_name || profile?.displayName || 'Unknown',
      user_id: original?.created_by?.user_id || profile?.id || '',
    },
    lastUpdateTimestamp: dayjs().unix(),
    updated_by: {
      display_name: profile?.displayName || 'Unknown',
      user_id: profile?.id || '',
    },
    communicationType: 7,
  };
};

export const EditIncident = ({ handleFormCancel, onDashboard = false, incidentData, responders }) => {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const { selfProfileRef, incidentTypes, incidentAssigneeStatusList } = useERSContext();
  const {
    plotDraggableIncidentMarker,
    createIncidentMarker,
    endCreateIncidentFlow,
    setRespondersForCreateIncident,
    setEditingIncidentAndCandidates,
  } = useMapContext();
  const [incidentTypesOptions] = useState(
    incidentTypes?.map(item => {
      return {
        value: item?.display_name,
        label:
          t('INCIDENT_TYPES', { returnObjects: true }).find(type => type.value === item?.display_name)?.label ||
          item?.display_name,
      };
    }) || []
  );
  const [originalIncidentData] = useState(() =>
    JSON.parse(JSON.stringify(getInitialFormData(incidentData, selfProfileRef?.current, incidentTypesOptions)))
  );
  const [formData, setFormData] = useState(originalIncidentData);
  const [errors, setErrors] = useState({
    description: '',
    location: '',
    location_description: '',
    status: '',
    assignees: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const lastSubmitTime = useRef(0);
  const selectedValues = (formData.assignees || []).map(a => a.id);
  const [respondersWithDistance, setRespondersWithDistance] = useState(responders);
  const [lastResponderLocations, setLastResponderLocations] = useState({});

  // location input states
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const errorMsg = field => <p className="text-error-strong-active text-xs">{errors[field]}</p>;
  const selectedUsers = respondersWithDistance?.filter(opt => selectedValues.includes(opt?.id));

  //Code for allow drag and move the incident creation window
  const popupRef = useRef(null);
  const offset = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const onMouseDown = e => {
    if (!popupRef.current) return;
    isDragging.current = true;
    offset.current = {
      x: e.clientX - popupRef.current.offsetLeft,
      y: e.clientY - popupRef.current.offsetTop,
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  const onMouseMove = e => {
    if (!isDragging.current || !popupRef.current) return;
    popupRef.current.style.left = `${e.clientX - offset.current.x}px`;
    popupRef.current.style.top = `${e.clientY - offset.current.y}px`;
  };
  const onMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };
  const [initOffset] = useState(() => ({
    x: Math.abs(Math.floor(Math.random() * 30)) + 20,
    y: Math.floor(Math.random() * 30),
  }));

  useEffect(() => {
    // Validate required fields on mount if they have values
    const fieldsToValidate = ['description', 'location', 'location_details'];
    const newErrors = {};

    fieldsToValidate.forEach(field => {
      if (formData[field] && validationRules[field]) {
        const isValid = validationRules[field].regex.test(formData[field]);
        newErrors[field] = isValid ? '' : validationRules[field].message;
      }
    });

    setErrors(prev => ({ ...prev, ...newErrors }));

    setEditingIncidentAndCandidates({
      incident: incidentData.original,
      candidates: incidentData.original.assignees,
    });
  }, [incidentData]);

  useEffect(() => {
    if (!formData.location || formData.location.length < 2) {
      setSuggestions([]);
      return;
    }

    const gps = parseGPSInput(formData.location);

    const fetchLocations = async () => {
      setIsLoading(true);
      try {
        if (gps) {
          // Reverse geocode the GPS coordinates to get an address
          const address = await getAddressFromCoord(gps.lat, gps.lng, mapbox_accessToken);
          setSuggestions([
            {
              id: 'gps-input',
              text: `${gps.lat}, ${gps.lng}`,
              place_name: address || `GPS: ${gps.lat}, ${gps.lng}`,
              center: [gps.lng, gps.lat],
              place_type: ['coordinate'],
            },
          ]);
        } else {
          const res = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              formData.location
            )}.json?access_token=${mapbox_accessToken}&limit=5`
          );
          const data = await res.json();
          setSuggestions(data.features || []);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (formData.geoloc?.long && formData.geoloc?.lat) {
      plotDraggableIncidentMarker([formData.geoloc?.long, formData.geoloc?.lat]);
    }

    const timeout = setTimeout(fetchLocations, 300);
    return () => clearTimeout(timeout);
  }, [formData.location]);

  // Clear status/assignees errors when status changes (no assignee requirement)
  useEffect(() => {
    setErrors(prev => ({
      ...prev,
      status: '',
      assignees: '',
    }));
  }, [formData.selectedStatus, formData.assignees]);

  const normalizedResponders = respondersWithDistance.map(r => ({
    ...r,
    id: r.id.split('@')[0],
  }));

  const validateField = useCallback((field, value) => {
    if (!validationRules[field]) return '';

    // Security: Enforce length limits (guard non-string values)
    if (INPUT_LIMITS[field] && typeof value === 'string' && value.length >= INPUT_LIMITS[field]) {
      return `Maximum ${INPUT_LIMITS[field]} characters allowed`;
    }

    if (!value) return '';

    const isValid = validationRules[field].regex.test(value);
    return isValid ? '' : validationRules[field].message;
  }, []);

  const handleChange = useCallback(
    (field, value) => {
      // Security: Sanitize input
      const sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value;

      setFormData(prev => ({
        ...prev,
        [field]: sanitizedValue,
      }));

      // Validate
      const errorMessage = validateField(field, sanitizedValue);
      setErrors(prev => ({
        ...prev,
        [field]: errorMessage,
      }));
    },
    [validateField]
  );

  const handleCancel = () => {
    setEditingIncidentAndCandidates(null);
    setFormData({
      id: uuidv4(),
      organizationId: selfProfileRef?.current?.organizationId,
      selectedIncidentType: null,
      selectedSeverity: null,
      selectedStatus: null,
      description: '',
      geoloc: { lat: null, long: null },
      location: '',
      location_description: '',
      creationTimestamp: dayjs().unix(),
      created_by: {
        display_name: selfProfileRef?.current?.displayName || 'NA',
        user_id: selfProfileRef?.current?.id || '',
      },
      updated_by: {
        display_name: selfProfileRef?.current?.displayName || 'NA',
        user_id: selfProfileRef?.current?.id || '',
      },
      lastUpdateTimestamp: dayjs().unix(),
      communicationType: 7,
    });
    handleFormCancel();
    endCreateIncidentFlow();
    setRespondersForCreateIncident([]);
  };

  const handleSelectLocation = async (data, isInput = false) => {
    try {
      let lat, long, address;
      if (isInput && data?.coordinates) {
        lat = data.coordinates.latitude;
        long = data.coordinates.longitude;
        address = data.display_name || data.placeName;
      } else {
        const coord = await getCurrentCoord();
        lat = coord.lat;
        long = coord.long;
        address = await getAddressFromCoord(lat, long, mapbox_accessToken);
      }

      // Set initial location in form
      setFormData(prev => ({
        ...prev,
        geoloc: { lat, long },
        location: address,
      }));

      const updatedResponderWithDistance = await updateRespondersDistance(
        responders,
        { lat, long },
        mapbox_accessToken
      );
      setRespondersWithDistance(updatedResponderWithDistance);
      console.log('Map window closed');
    } catch (err) {
      console.error('Error selecting location:', err);
    }
  };

  // Add this helper function to check if form has changes
  const hasChanges = useCallback(() => {
    // Check simple fields
    if (formData.description !== originalIncidentData.description) return true;
    if (formData.location !== originalIncidentData.location) return true;
    if (formData.location_description !== originalIncidentData.location_description) return true;

    // Check dropdown selections by value
    if (formData.selectedIncidentType?.value !== originalIncidentData.selectedIncidentType?.value) return true;
    if (formData.selectedSeverity?.value !== originalIncidentData.selectedSeverity?.value) return true;

    // For status: compare values, treating null original as "no change needed unless user selected something"
    const originalStatusValue = originalIncidentData.selectedStatus?.value || null;
    const currentStatusValue = formData.selectedStatus?.value || null;
    if (currentStatusValue !== originalStatusValue) return true;

    // Check geoloc
    if (JSON.stringify(formData.geoloc) !== JSON.stringify(originalIncidentData.geoloc)) return true;

    // Check assignees
    if (JSON.stringify(formData.assignees) !== JSON.stringify(originalIncidentData.assignees)) return true;

    return false;
  }, [originalIncidentData, formData]);

  const checks = {
    noDescription: !formData.description,
    noLocation: !formData.location,
    noIncidentType: !formData.selectedIncidentType?.value,
    noSeverity: !formData.selectedSeverity?.value,
    hasLat: !formData.geoloc?.lat,
    hasLong: !formData.geoloc?.long,
    errorDescription: !!errors.description,
    errorLocation: !!errors.location,
    errorStatus: !!errors.status,
    errorAssignees: !!errors.assignees,
    isSubmitting,
    noChanges: !hasChanges(),
  };

  const isCompleteDisabled = Object.values(checks).some(Boolean);

  const getChangedFields = (original, updated) => {
    const changes = {};
    Object.keys(updated).forEach(key => {
      const originalValue = original[key];
      const updatedValue = updated[key];

      // Simple shallow compare for primitives and arrays
      if (Array.isArray(updatedValue)) {
        if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
          changes[key] = updatedValue;
        }
      } else if (typeof updatedValue === 'object' && updatedValue !== null) {
        // Optional: deep compare nested objects (or just send the whole object)
        if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
          changes[key] = updatedValue;
        }
      } else if (updatedValue !== originalValue) {
        changes[key] = updatedValue;
      }
    });
    return changes;
  };

  // Security & Performance: Rate-limited submit with comprehensive error handling
  const handleComplete = useCallback(async () => {
    setEditingIncidentAndCandidates(null);
    // Performance: Rate limiting
    const now = Date.now();
    if (now - lastSubmitTime.current < SUBMIT_DEBOUNCE_MS) {
      setSubmitError(t('ERRORS.WAIT_BEFORE_SUBMIT_AGAIN'));
      return;
    }
    lastSubmitTime.current = now;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const sdk = window?.wbSdk?.incidentManager;
      // Validate SDK availability
      if (!sdk?.updateIncident) {
        throw new Error(t('ERRORS.INCIDENT_CREATION_SERVICE_UNAVAILABLE'));
      }

      // Compute changed fields
      const changedFields = getChangedFields(originalIncidentData, formData);

      // If no fields changed, skip update
      if (Object.keys(changedFields).length === 0) {
        setSubmitError(t('ERRORS.NO_CHANGES_TO_UPDATE'));
        setIsSubmitting(false);
        return;
      }

      // Build location object with proper nesting
      let locationPayload;
      if (typeof formData.location === 'string') {
        // If location is a string, create object with display_name
        locationPayload = {
          display_name: sanitizeInput(formData.location),
        };
      } else {
        // If location is already an object, use it as base
        locationPayload = { ...formData.location };
      }

      if (formData.location_description?.trim()) {
        // Add location_description if provided (nested inside location object)
        locationPayload.location_description = sanitizeInput(formData.location_description);
      }

      // payload only changed fields
      const updatePayload = {
        id: formData.id,
        type: formData.selectedIncidentType?.value || null,
        description: sanitizeInput(formData.description),
        severity: formData.selectedSeverity?.value ?? null,
        priority: formData.selectedSeverity?.value ?? null,
        status: formData.selectedStatus?.value || constants.TAB_KEYS.TAB_VALUE_IN_PROGRESS,
        geoloc: formData.geoloc,
        location: locationPayload,
        lastUpdateTimestamp: formData.lastUpdateTimestamp,
        updatedBy: formData.updated_by,
      };

      // ---- Execute SDK Updates ----

      // 1.Update general incident details
      const updateIncident = await sdk.updateIncident(updatePayload);
      showToast({
        type: constants?.MESSAGE?.INCIDENT,
        title: updateIncident?.title,
        description: `${t('DASHBOARD.INCIDENT_UPDATED_BY')} ${updateIncident?.updated_by?.display_name} `,
        editeddata: updateIncident,
      });

      const originalAssigneeIds = (originalIncidentData.assignees || []).map(a => a.id);
      const newAssigneeIds = (formData.assignees || []).map(a => a.id);
      const toAdd = newAssigneeIds.filter(id => !originalAssigneeIds.includes(id));
      const toRemove = originalAssigneeIds.filter(id => !newAssigneeIds.includes(id));

      const originalAssignees = originalIncidentData.assignees || [];
      const currentAssignees = formData.assignees || [];

      if (toAdd.length > 0 || toRemove.length > 0) {
        await sdk.updateIncidentAssignees(formData.id, { toAdd, toRemove });
      }
      // Update each assignee’s response status
      for (const assignee of currentAssignees) {
        const original = originalAssignees.find(a => a.id === assignee.id);
        if (original && original.response_status !== assignee.response_status) {
          // Call SDK to update assignee response status
          await sdk.changeAssigneeResponseStatus(formData.id, assignee.id, assignee.response_status);
        }
      }
      //update assignee status
      for (const assignee of currentAssignees) {
        // Find the matching original assignee
        const original = originalAssignees.find(a => a.id === assignee.id);

        if (original) {
          // --- Deep copy to avoid mutating references ---
          const originalStatuses = JSON.parse(JSON.stringify(original.status_records));
          const currentStatuses = JSON.parse(JSON.stringify(assignee.status_records));

          // Find all new status objects that exist in current but not in original
          const newStatusObjects = currentStatuses.filter(cs => !originalStatuses.some(os => os.status === cs.status));

          // If there are new statuses, call SDK for each one
          for (const newStatusObj of newStatusObjects) {
            const newStatusId = newStatusObj.status;

            await sdk.setAssigneeStatus(formData.id, assignee.id, newStatusId);
          }
        }
      }

      handleFormCancel();
      endCreateIncidentFlow();
      setRespondersForCreateIncident([]);
    } catch (error) {
      console.error('Failed to update incident:', error);
      setSubmitError(error.message || t('ERRORS.FAILED_TO_UPDATE_INCIDENT'));
      showToast({ type: constants?.MESSAGE?.ERROR, title: t('ERRORS.FAILED_TO_CREATE_INCIDENT') });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedValues, handleFormCancel, selectedUsers]);

  useEffect(() => {
    const fetchAndSetAddress = async () => {
      if (!createIncidentMarker?.coord || createIncidentMarker.coord.length !== 2) return;
      try {
        const [long, lat] = createIncidentMarker.coord;

        const address = await getAddressFromCoord(lat, long, mapbox_accessToken);

        setFormData(prev => ({
          ...prev,
          geoloc: { lat, long },
          location: address,
        }));

        const updatedResponderWithDistance = await updateRespondersDistance(
          responders,
          { lat, long },
          mapbox_accessToken
        );
        setRespondersWithDistance(updatedResponderWithDistance);
      } catch (err) {
        console.error('Error fetching address:', err);
      }
    };

    fetchAndSetAddress();
  }, [createIncidentMarker]);

  useEffect(() => {
    const updateResponderDistances = async () => {
      try {
        if (!responders?.length) return;

        const coords = createIncidentMarker?.coord?.length
          ? { lat: createIncidentMarker.coord[1], long: createIncidentMarker.coord[0] }
          : formData?.geoloc;

        if (!coords?.lat || !coords?.long) return;

        let movedResponders = [];
        let anyResponderMoved = false;

        for (const responder of responders) {
          const responderCoords = [responder?.lastKnownLocation?.lon, responder?.lastKnownLocation?.lat];

          if (!isValidCoordinates(responderCoords)) continue;

          const lastCoords = lastResponderLocations[responder.id];
          const hasMoved = !lastCoords || lastCoords[0] !== responderCoords[0] || lastCoords[1] !== responderCoords[1];

          if (hasMoved) {
            anyResponderMoved = true;
            movedResponders.push(responder);

            // update stored coordinates
            setLastResponderLocations(prev => ({
              ...prev,
              [responder.id]: responderCoords,
            }));
          }
        }

        // No movement? Skip API
        if (!anyResponderMoved) {
          return;
        }

        // Update only moved responders' distances
        const updatedMovedResponders = await updateRespondersDistance(movedResponders, coords, mapbox_accessToken);

        // Merge with existing distances (keep old ones)
        setRespondersWithDistance(prev => {
          const prevMap = new Map(prev.map(r => [r.id, r]));
          for (const updated of updatedMovedResponders) {
            prevMap.set(updated.id, updated); // replace moved one with updated
          }
          return Array.from(prevMap.values());
        });
      } catch (err) {
        console.error('Error updating responder distances:', err);
      }
    };

    updateResponderDistances();
  }, [responders, createIncidentMarker?.coord, formData?.geoloc?.lat, formData?.geoloc?.long]);

  const handleResponderHover = async (options, isHovering) => {
    if (isHovering) {
      const updatedResponder = await fetchRouteAndUpdateResponder({
        responder: options,
        incidentLocation: formData,
        isSelected: false,
        isHover: true,
      });
      setRespondersForCreateIncident(prev => {
        const exists = prev.find(r => r.id === options?.id);
        if (exists) {
          return prev.map(r => (r.id === options.id ? { ...r, isHover: true } : r));
        }
        return [...prev, updatedResponder];
      });
    } else {
      setRespondersForCreateIncident(prev => prev.filter(r => !(r.id === options?.id && r.isHover && !r.isSelected)));
    }
  };

  const onResponseStatusChange = (assigneeId, newStatus) => {
    // Update response status in formData.assignees
    setFormData(prev => {
      const updatedAssignees =
        prev.assignees?.map(a => (a.id === assigneeId ? { ...a, response_status: newStatus } : a)) || [];

      // If assignee doesn't exist yet, add them
      if (!updatedAssignees.find(a => a.id === assigneeId)) {
        updatedAssignees.push({
          id: assigneeId,
          display_name: item.displayName,
          response_status: newStatus,
          status_records: [],
        });
      }

      return { ...prev, assignees: updatedAssignees };
    });
  };
  const onCurrentStatusChange = (assigneeId, newStatusId) => {
    // Add new status record to this assignee
    setFormData(prev => {
      const updatedAssignees =
        prev.assignees?.map(a => {
          if (a.id === assigneeId) {
            const newRecord = {
              status: newStatusId,
              timestamp: dayjs().unix(),
            };
            return {
              ...a,
              status_records: [...(a.status_records || []), newRecord],
            };
          }
          return a;
        }) || [];

      // If assignee doesn't exist yet, create them
      if (!updatedAssignees.find(a => a.id === assigneeId)) {
        updatedAssignees.push({
          id: assigneeId,
          display_name: item.displayName,
          response_status: 'pending',
          status_records: [
            {
              status: newStatusId,
              timestamp: dayjs().unix(),
            },
          ],
        });
      }

      return { ...prev, assignees: updatedAssignees };
    });
  };

  useEffect(() => {
    // Sync formData.assignees with selectedValues
    const currentAssigneeIds = formData.assignees?.map(a => a.id) || [];

    // Find newly selected responders (in selectedValues but not in assignees)
    const newlySelected = selectedValues.filter(id => !currentAssigneeIds.includes(id));

    // Find deselected responders (in assignees but not in selectedValues)
    const deselected = currentAssigneeIds.filter(id => !selectedValues.includes(id));

    if (newlySelected.length > 0 || deselected.length > 0) {
      setFormData(prev => {
        // Remove deselected assignees
        let updatedAssignees = prev.assignees?.filter(a => !deselected.includes(a.id)) || [];

        // Add newly selected assignees with default values
        const newAssignees = newlySelected.map(id => {
          // Match the id properly - remove @domain if present
          const normalizedId = id.split('@')[0];
          const responder = respondersWithDistance.find(r => {
            const responderId = r.id.split('@')[0];
            return responderId === normalizedId || r.id === id;
          });

          return {
            id: id,
            display_name: responder?.displayName || responder?.display_name || 'Unknown',
            displayName: responder?.displayName || responder?.display_name || 'Unknown',
            presence: responder?.presence || 'offline',
            response_status: 'pending',
            status_records: [],
          };
        });

        updatedAssignees = [...updatedAssignees, ...newAssignees];

        return {
          ...prev,
          assignees: updatedAssignees,
        };
      });
    }
  }, [selectedValues, respondersWithDistance]);

  const renderContent = (
    <div
      ref={popupRef}
      onMouseDown={() => {
        if (popupRef.current) {
          popupRef.current.style.zIndex = Date.now() % 86400000;
        }
      }}
      className="fixed bg rounded-xl shadow-lg text-sm flex flex-col border border-border min-w-[340px] h-3/4 overflow-hidden"
      style={{
        zIndex: 100,
        left: `${initOffset.x}px`,
        top: `${initOffset.y}px`,
        position: 'fixed',
        width: getStoredSizes()?.leftSidebarPan?.value * (getStoredSizes()?.leftSidebarPan?.incidentPan / 100) + '%',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between bg-subtle border-b border-border px-3 py-2 cursor-move"
        onMouseDown={onMouseDown}
      >
        <span className="font-medium text-sm">{`Edit: ${incidentData?.original?.title}`}</span>
        <button
          className="p-1 hover:bg-strong rounded transition-colors"
          disabled={isSubmitting}
          onClick={handleCancel}
          aria-label="Close"
        >
          <X size={18} className="text-muted" />
        </button>
      </div>
      {/* Global Error Display */}
      {submitError && (
        <div
          className="mx-3 mt-3 p-3 bg-error-subtle border border-error-strong rounded-lg flex items-start gap-2"
          role="alert"
        >
          <AlertCircle size={16} className="text-error-strong mt-0.5 flex-shrink-0" />
          <p className="text-sm text-error-strong">{submitError}</p>
        </div>
      )}

      {/* Body */}
      <div className="flex flex-col flex-1 gap-3 p-3 overflow-y-auto">
        <div className="flex flex-row gap-4">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{t('INCIDENTS_PANEL.REPORTED_DATE_TIME')}</Label>
            <span className="text-muted">
              {dayjs(formData?.creationTimestamp * 1000).format('YYYY-MM-DD HH:mm:ss')}
            </span>
          </div>
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{t('INCIDENTS_PANEL.REPORTED_NAME')}</Label>
            <span className="text-muted">{formData?.created_by?.display_name}</span>
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{capitalizeWords(t('INCIDENTS_PANEL.DESCRIPTION'))}</Label>
            <Textarea
              className="pb-6 flex-1"
              placeholder={t('PLACEHOLDER.DESCRIPTION')}
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
              maxLength={INPUT_LIMITS.description}
            />
            {errors.description && errorMsg('description')}
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <Label className={`${asterisk} whitespace-nowrap`}>{t('INCIDENTS_PANEL.INCIDENT_TYPE')}</Label>
          <Combobox
            data={incidentTypesOptions}
            selectedData={formData.selectedIncidentType}
            onChange={selectedIncidentType => setFormData(prev => ({ ...prev, selectedIncidentType }))}
            placeholder={t('PLACEHOLDER.SELECT_OPTION')}
          />
        </div>
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{capitalizeWords(t('INCIDENTS_PANEL.LOCATION'))}</Label>
            <div className="flex flex-row gap-x-2 align-middle">
              <LocationInput
                value={formData.location}
                onInputChange={location =>
                  setFormData(prev => ({ ...prev, location, geoloc: { lat: null, long: null } }))
                }
                onChange={data => handleSelectLocation(data, true)}
                isLoading={isLoading}
                suggestions={suggestions}
                placeholder={t('PLACEHOLDER.LOCATION')}
                error={null}
                handleSelectedClear={() => {
                  setFormData(prev => ({ ...prev, location: '', geoloc: { lat: null, long: null } }));
                }}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const coord = await getCurrentCoord();
                  if (formData.geoloc?.long && formData.geoloc?.lat) {
                    plotDraggableIncidentMarker([formData.geoloc?.long, formData.geoloc?.lat]);
                  } else {
                    plotDraggableIncidentMarker([coord.long, coord.lat]);
                  }
                }}
              >
                <Locate size={13} />
              </Button>
            </div>
            {errors.location && errorMsg('location')}
          </div>
        </div>
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{capitalizeWords(t('INCIDENTS_PANEL.LOCATION_DESCRIPTION'))}</Label>
            <div className="flex flex-row align-middle">
              <Input
                value={formData.location_description}
                placeholder={t('PLACEHOLDER.LOCATION_DESCRIPTION')}
                onChange={e => handleChange('location_description', e.target.value)}
              />
            </div>
            {errors.location && errorMsg('location_description')}
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{capitalizeWords(t('INCIDENTS_PANEL.SEVERITY'))}</Label>
            <Combobox
              data={t('SEVERITY_VALUES', { returnObjects: true })}
              selectedData={formData.selectedSeverity}
              onChange={selectedSeverity => setFormData(prev => ({ ...prev, selectedSeverity }))}
              placeholder={t('PLACEHOLDER.SELECT_OPTION')}
            />
          </div>
          <div className="flex flex-1 flex-col gap-y-1">
            <Label className={asterisk}>{capitalizeWords(t('INCIDENTS_PANEL.STATUS'))}</Label>
            <Combobox
              data={t('EDIT_INCIDENT_STATUS_VALUES', { returnObjects: true })}
              selectedData={formData.selectedStatus?.value === 'open' ? null : formData.selectedStatus}
              onChange={selectedStatus => {
                // If user tries to clear/deselect, revert to original status
                if (!selectedStatus) {
                  setFormData(prev => ({ ...prev, selectedStatus: originalIncidentData.selectedStatus }));
                } else {
                  setFormData(prev => ({ ...prev, selectedStatus }));
                }
              }}
              placeholder={
                formData.selectedStatus?.value === 'open'
                  ? t('TAB_KEYS.TAB_VALUE_CREATED')
                  : t('PLACEHOLDER.SELECT_STATUS')
              }
            />
            {errors.status && errorMsg('status')}
          </div>
        </div>
        <div className="flex flex-col">
          <div className="flex flex-1 flex-col gap-y-1">
            <Label>{capitalizeWords(t('INCIDENTS_PANEL.RESPONDERS'))}</Label>
            <div className="rounded-lg shadow-sm">
              <MultiSelectDropdown
                expandDirection="up"
                options={normalizedResponders || []}
                value={selectedValues}
                placeholder={t('INCIDENTS_PANEL.SELECT_RESPONDERS')}
                onChange={newSelectedIds => {
                  setEditingIncidentAndCandidates({
                    incident: incidentData.original,
                    candidates: newSelectedIds?.map(aID => ({ id: aID })) || [],
                  });
                  setFormData(prev => {
                    const prevAssignees = prev.assignees || [];

                    // Add new assignees
                    const newAssignees = newSelectedIds
                      .filter(id => !prevAssignees.some(a => a.id === id))
                      .map(id => {
                        const responder = normalizedResponders.find(r => r.id.split('@')[0] === id.split('@')[0]);
                        return {
                          id,
                          display_name: responder?.displayName || responder?.display_name || 'Unknown',
                          response_status: 'pending',
                          status_records: [],
                        };
                      });

                    // Remove unselected assignees
                    const updatedAssignees = prevAssignees.filter(a => newSelectedIds.includes(a.id));

                    return {
                      ...prev,
                      assignees: [...updatedAssignees, ...newAssignees],
                    };
                  });
                }}
                handleResponderHover={handleResponderHover}
              />
            </div>
            {errors.assignees && errorMsg('assignees')}
          </div>
          {selectedValues.length > 0 && (
            <div className="pt-3 grid gap-4 grid-cols-[repeat(auto-fit,minmax(400px,1fr))]">
              {selectedValues.map(selectedId => {
                // Normalize the selected ID
                const normalizedSelectedId = selectedId.split('@')[0];

                // Find the responder details with normalized matching
                const responder = respondersWithDistance.find(r => {
                  const responderId = r.id.split('@')[0];
                  return responderId === normalizedSelectedId || r.id === selectedId;
                });

                // Find or create assignee data from formData
                const assigneeData = formData.assignees?.find(a => a.id === selectedId) || {
                  id: selectedId,
                  display_name: responder?.displayName || responder?.display_name || 'Unknown',
                  displayName: responder?.displayName || responder?.display_name || 'Unknown',
                  presence: responder?.presence || 'offline',
                  response_status: 'pending',
                  status_records: [],
                };

                return (
                  <ResponderDetail
                    key={selectedId}
                    assignee={{
                      ...responder,
                      ...assigneeData,
                      display_name: assigneeData?.display_name || responder?.displayName,
                      displayName: assigneeData?.displayName || responder?.displayName,
                      presence: responder?.presence || assigneeData?.presence,
                    }}
                    statusList={incidentAssigneeStatusList}
                    mode="edit"
                    onClose={() => {
                      setFormData(prev => ({
                        ...prev,
                        assignees: (prev.assignees || []).filter(a => a.id !== selectedId),
                      }));
                    }}
                    onResponseStatusChange={onResponseStatusChange}
                    onCurrentStatusChange={onCurrentStatusChange}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-border">
        <Button
          aria-label="Close incident editor"
          onClick={handleCancel}
          disabled={isSubmitting}
          size="sm"
          variant="outline"
        >
          {t('INCIDENTS_PANEL.CANCEL')}
        </Button>
        <Button onClick={handleComplete} disabled={isCompleteDisabled} size="sm" variant="default">
          {t('INCIDENTS_PANEL.SAVE')}
        </Button>
      </div>
    </div>
  );

  return createPortal(renderContent, document.body);
};
